import { memo } from "react";
import * as Styled from "./style";
import { PlayButton } from "~/components/blocks/common/play-button";
import { getPlayButtonText } from "~/components/blocks/product/detail/header/video-season-header/play-icon-button/util/get-play-button-text";
import type { VideoSeasonType } from "~/components/blocks/product/type-guard";
import { useQueryString } from "~/hooks/use-query-string";
import { G_VideoSeasonType } from "~/lib/graphql";

type PlayIconButtonProps = {
  seasonType: G_VideoSeasonType;
  continueWatching: VideoSeasonType["continueWatching"];
  isStreamable: boolean;
  ppvProducts: VideoSeasonType["episodes"]["edges"][number]["node"]["ppvProducts"];
  svodProduct: VideoSeasonType["episodes"]["edges"][number]["node"]["svodProduct"];
  episodeContentId: string;
  episodeTitle: string;
  episodeNumber: string;
  episodeNumberName: VideoSeasonType["episodes"]["edges"][number]["node"]["episodeNumberName"];
  playInfo?: NonNullable<VideoSeasonType["episode"]>["playInfo"];
  isPlayBtnDisplay: boolean;
  onPlayBtnPressed: () => void;
};

/**
 * VideoSeason の再生ボタンのロジックに責務を持つコンポーネント
 * - レジュームがあれば「続きから再生」| レジュームがなければ「再生」
 */
export const PlayIconButton = memo((props: PlayIconButtonProps) => {
  const {
    seasonType,
    continueWatching,
    isStreamable,
    ppvProducts,
    svodProduct,
    episodeContentId,
    episodeTitle,
    episodeNumber,
    episodeNumberName,
    playInfo,
    isPlayBtnDisplay,
    onPlayBtnPressed,
  } = props;

  const { content } = useQueryString();

  const isSingleEpisode = seasonType === G_VideoSeasonType.SingleEpisode;

  const playButtonText = getPlayButtonText({
    continueWatching,
    isStreamable,
    ppvProducts,
    svodProduct,
    episodeContentId,
    isSingleEpisode,
    isEpisodeDetail: content !== undefined,
    episodeTitle,
    episodeNumber,
    episodeNumberName,
    playInfo,
  });
  if (playButtonText === null) return null;

  const { episodeName, episodeSubText } = playButtonText;

  return (
    <Styled.DivPlayPositionCenter isPlayBtnDisplay={isPlayBtnDisplay} data-testid="play-icon-button-wrapper">
      <PlayButton onClick={onPlayBtnPressed} />

      <Styled.DivTextFlex>
        {episodeName && <Styled.ParOneLineText>{episodeName}</Styled.ParOneLineText>}
        <Styled.ParText>{episodeSubText}</Styled.ParText>
      </Styled.DivTextFlex>
    </Styled.DivPlayPositionCenter>
  );
});

PlayIconButton.displayName = "PlayIconButton";
